import { PRICE_CHANGED, PRICE_CHANGED_SEATSELL1 } from '@/src/constants/itineraryStatus'

import {
    Fare,
    ItineraryResponse,
    SmbMandatoryFareMapType,
    SmbMandatorySegmentMapType,
    VasAncillary
} from '@/src/types/itinerary'

import { getFareCtBrandName } from '@/src/utils/itinerary/getFareCtBrandName'
import {
    getFares,
    getInsurances,
    getSelectedInsurance,
    getSelectedTicketedVas,
    getTicketedVasList
} from '@/src/utils/itinerary/flightTransformers'

/** Prefer populated `vasAncillaries`; otherwise use itin-ex `vas`. */
const resolveVasAncillariesList = (raw: ItineraryResponse): VasAncillary[] => {
    const { vasAncillaries: legacy, vas } = raw
    if (Array.isArray(legacy) && legacy.length > 0) return legacy
    if (Array.isArray(vas) && vas.length > 0) return vas
    return Array.isArray(legacy) ? legacy : []
}

export const transformItineraryData = (itineraryData: ItineraryResponse) => {
    const raw = itineraryData || ({} as ItineraryResponse)
    const vasList = resolveVasAncillariesList(raw)

    const normalizedItineraryData: ItineraryResponse = {
        ...raw,
        vasAncillaries: vasList
    }

    const {
        flights,
        itineraryId,
        itineraryStatus,
        searchCriteria,
        meta,
        passengers,
        customer,
        fares,
        updatedFareResponse,
        currencyConversionDetails,
        paxPricingInfoJson
    } = normalizedItineraryData

    const faresList = flights ? getFares(flights, paxPricingInfoJson, vasList) : []
    const insuranceList = vasList.length ? getInsurances(vasList) : []
    const ticketedVasList = vasList.length ? getTicketedVasList(vasList) : []
    // One selection bucket for every VAS type — pricing and the update payload read it as a whole.
    const selectedInsurances = [
        ...(insuranceList ? getSelectedInsurance(insuranceList) : []),
        ...getSelectedTicketedVas(ticketedVasList)
    ]
    const isInsuranceTermsAndConditionsChecked = true
    const isPriceChange = [PRICE_CHANGED_SEATSELL1, PRICE_CHANGED].includes(itineraryStatus)

    const fareFamilyEnabledOnReview = meta?.fareFamilyEnabledOnReview
    let fareFamilyFares: Fare[] = []
    if (fareFamilyEnabledOnReview) {
        fareFamilyFares = fares
    }

    const smbMandatoryFareMap: SmbMandatoryFareMapType = {}

    if (fares?.length && flights?.length) {
        fares.forEach(fare => {
            const ctBrandName = getFareCtBrandName(fare, itineraryId)

            const smbMandatorySegmentMap: SmbMandatorySegmentMapType = {}

            if (fare.fareGroupInformationMap) {
                for (const [key, fareGroup] of Object.entries(fare.fareGroupInformationMap)) {
                    const flightIndex = parseInt(key, 10) - 1
                    const flight = flights[flightIndex]

                    // Map each segment to its SMB mandatory settings
                    flight.segments.forEach(segment => {
                        const segmentKey = `${segment.departAirport}_${segment.arrivalAirport}`
                        smbMandatorySegmentMap[segmentKey] = {
                            seat: fareGroup.seatMandatory,
                            meal: fareGroup.mealMandatory
                        }
                    })
                }
                smbMandatoryFareMap[ctBrandName] = smbMandatorySegmentMap
            }
        })
    }

    return {
        itineraryData: normalizedItineraryData,
        flights,
        itineraryStatus,
        searchCriteria,
        faresList,
        meta,
        passengers,
        customer,
        fareFamilyFares,
        currencyConversionDetails,
        insurance: {
            insuranceList,
            ticketedVasList,
            selectedInsurances,
            isInsuranceTermsAndConditionsChecked
        },
        metaData: {
            priceChangePopupData: {
                updatedFareResponse: updatedFareResponse || {},
                isPriceChange,
                priceChangePopupShown: false,
                oldPrice: updatedFareResponse?.oldPrice || 0,
                newPrice: updatedFareResponse?.totalPrice || 0
            }
        },
        smbMandatoryFareMap
    }
}
